import React, { useState } from 'react';
import {
  useApiQuery,
  useApiMutation,
} from '../hooks/useApiQuery';
import {
  getTicketTypes,
  createTicketType,
  updateTicketType,
  deleteTicketType,
} from '../services/api';
import useToast from '../hooks/useToast';

const TicketTypesManager = ({ eventId }) => {
  const { showSuccess, showError } = useToast();

  const {
    data: ticketTypes = [],
    isLoading,
    refetch,
  } = useApiQuery(
    ['ticket-types', eventId],
    () => getTicketTypes(eventId).then(r => r.data),
    { enabled: Boolean(eventId) }
  );

  // null = closed, {} = new, {...} = edit
  const [editing, setEditing] = useState(null);

  const createMutation = useApiMutation(
    data => createTicketType(eventId, data),
    {
      onSuccess: () => {
        showSuccess('Ticket type created');
        setEditing(null);
        refetch();
      },
      onError: e =>
        showError(e.response?.data?.message || 'Failed'),
    }
  );

  const updateMutation = useApiMutation(
    ({ id, data }) => updateTicketType(eventId, id, data),
    {
      onSuccess: () => {
        showSuccess('Ticket type updated');
        setEditing(null);
        refetch();
      },
      onError: e =>
        showError(e.response?.data?.message || 'Failed'),
    }
  );

  const deleteMutation = useApiMutation(
    id => deleteTicketType(eventId, id),
    {
      onSuccess: () => {
        showSuccess('Ticket type deleted');
        refetch();
      },
      onError: e =>
        showError(e.response?.data?.message || 'Failed'),
    }
  );

  const handleSave = () => {
    if (!editing.name?.trim()) {
      showError('Name is required');
      return;
    }

    if (editing._id) {
      updateMutation.mutate({
        id: editing._id,
        data: {
          name: editing.name,
          color: editing.color,
          description: editing.description,
        },
      });
    } else {
      createMutation.mutate({
        name: editing.name,
        color: editing.color,
        description: editing.description,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-[#708090]">
        Loading ticket types...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#2b3445]">
          Ticket Types
        </h3>
        <button
          onClick={() =>
            setEditing({
              name: '',
              color: '#1471b9',
              description: '',
            })
          }
          className="bg-[#1471b9] text-white px-4 py-2 rounded-[8px] text-sm font-semibold hover:opacity-90"
        >
          + Add Ticket Type
        </button>
      </div>

      <div className="bg-white rounded-[15px] border border-[#dae1e7] divide-y divide-[#dae1e7]">
        {ticketTypes.length === 0 && (
          <div className="p-6 text-center text-sm text-[#708090]">
            No ticket types yet. Add one to get started.
          </div>
        )}

        {ticketTypes.map(t => (
          <div
            key={t._id}
            className="flex items-center justify-between p-4"
          >
            <div className="flex items-center gap-3">
              <span
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: t.color }}
              />
              <div>
                <p className="font-semibold text-[#2b3445]">
                  {t.name}
                </p>
                {t.description && (
                  <p className="text-xs text-[#708090] mt-0.5">
                    {t.description}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setEditing({ ...t })}
                className="text-[#1471b9] text-sm font-semibold hover:underline"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  if (window.confirm(`Delete "${t.name}"?`)) {
                    deleteMutation.mutate(t._id);
                  }
                }}
                className="text-red-600 text-sm font-semibold hover:underline"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-[15px] p-6 w-[420px] shadow-xl">
            <h3 className="text-lg font-semibold mb-4 text-[#2b3445]">
              {editing._id ? 'Edit Ticket Type' : 'New Ticket Type'}
            </h3>

            <label className="block text-sm font-semibold text-[#2b3445] mb-1">
              Name *
            </label>
            <input
              type="text"
              value={editing.name}
              onChange={e =>
                setEditing({ ...editing, name: e.target.value })
              }
              className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm mb-3"
              placeholder="VIP"
            />

            <label className="block text-sm font-semibold text-[#2b3445] mb-1">
              Color
            </label>
            <input
              type="color"
              value={editing.color}
              onChange={e =>
                setEditing({ ...editing, color: e.target.value })
              }
              className="w-16 h-10 border border-[#dae1e7] rounded-[8px] mb-3"
            />

            <label className="block text-sm font-semibold text-[#2b3445] mb-1">
              Description
            </label>
            <input
              type="text"
              value={editing.description || ''}
              onChange={e =>
                setEditing({ ...editing, description: e.target.value })
              }
              className="w-full border border-[#dae1e7] rounded-[8px] px-3 py-2 text-sm mb-4"
              placeholder="Optional note"
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-[8px] text-sm font-semibold text-[#708090] hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={
                  createMutation.isPending ||
                  updateMutation.isPending
                }
                className="bg-[#1471b9] text-white px-4 py-2 rounded-[8px] text-sm font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {editing._id ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketTypesManager;